import { ZodFirstPartyTypeKind, type ZodTypeAny } from 'zod';

type ZodDef = Record<string, unknown>;
type Resolver = (def: ZodDef, recurse: (s: ZodTypeAny) => unknown) => unknown;

interface ZodCheck {
  kind: string;
  value?: unknown;
  inclusive?: boolean;
}

const STRING_CHECK_VALUES: Record<string, string> = {
  email: 'user@example.com',
  url: 'https://example.com',
  datetime: '2025-01-01T00:00:00.000Z',
  uuid: '00000000-0000-0000-0000-000000000000',
  cuid: 'clxxxxxxxxxxxxxxxxxxxxxxxxx',
  ip: '0.0.0.0',
};

function resolveString(def: ZodDef): string {
  const checks: ZodCheck[] = (def.checks as ZodCheck[] | undefined) ?? [];
  for (const check of checks) {
    const placeholder = STRING_CHECK_VALUES[check.kind];
    if (placeholder) return placeholder;
  }
  const exactLen = checks.find((c) => c.kind === 'length');
  if (exactLen) return ' '.repeat(exactLen.value as number);
  const minLen = checks.find((c) => c.kind === 'min');
  if (minLen && (minLen.value as number) > 0) return ' '.repeat(minLen.value as number);
  return '';
}

function resolveNumber(def: ZodDef): number {
  const checks: ZodCheck[] = (def.checks as ZodCheck[] | undefined) ?? [];
  let min = 0;
  for (const check of checks) {
    if (check.kind === 'min') {
      const v = check.value as number;
      min = Math.max(min, check.inclusive === false ? v + 1 : v);
    }
  }
  const isInt = checks.some((c) => c.kind === 'int');
  return isInt ? Math.ceil(min) : min || 0;
}

const RESOLVERS: Partial<Record<ZodFirstPartyTypeKind, Resolver>> = {
  [ZodFirstPartyTypeKind.ZodObject]: (def, recurse) => {
    const shape = (def.shape as () => Record<string, ZodTypeAny>)();
    return Object.fromEntries(Object.entries(shape).map(([k, v]) => [k, recurse(v)]));
  },

  [ZodFirstPartyTypeKind.ZodArray]: (def, recurse) => [recurse(def.type as ZodTypeAny)],

  [ZodFirstPartyTypeKind.ZodString]: resolveString,
  [ZodFirstPartyTypeKind.ZodNumber]: resolveNumber,
  [ZodFirstPartyTypeKind.ZodBoolean]: () => false,

  [ZodFirstPartyTypeKind.ZodEnum]: (def) => (def.values as unknown[])[0],
  [ZodFirstPartyTypeKind.ZodNativeEnum]: (def) => {
    const vals = Object.values(def.values as Record<string, unknown>);
    return vals[0] ?? null;
  },
  [ZodFirstPartyTypeKind.ZodLiteral]: (def) => def.value as unknown,

  [ZodFirstPartyTypeKind.ZodDefault]: (def) => (def.defaultValue as () => unknown)(),

  [ZodFirstPartyTypeKind.ZodOptional]: (def, recurse) => recurse(def.innerType as ZodTypeAny),
  [ZodFirstPartyTypeKind.ZodNullable]: (def, recurse) => recurse(def.innerType as ZodTypeAny),

  [ZodFirstPartyTypeKind.ZodEffects]: (def, recurse) => recurse(def.schema as ZodTypeAny),

  [ZodFirstPartyTypeKind.ZodUnion]: (def, recurse) => recurse((def.options as ZodTypeAny[])[0]),
  [ZodFirstPartyTypeKind.ZodDiscriminatedUnion]: (def, recurse) =>
    recurse((def.options as ZodTypeAny[])[0]),

  [ZodFirstPartyTypeKind.ZodIntersection]: (def, recurse) => ({
    ...(recurse(def.left as ZodTypeAny) as object),
    ...(recurse(def.right as ZodTypeAny) as object),
  }),

  [ZodFirstPartyTypeKind.ZodRecord]: () => ({}),
  [ZodFirstPartyTypeKind.ZodUnknown]: () => null,
  [ZodFirstPartyTypeKind.ZodAny]: () => null,

  [ZodFirstPartyTypeKind.ZodLazy]: (def, recurse) => recurse((def.getter as () => ZodTypeAny)()),
};

/**
 * Derives a complete example value from a Zod schema by recursively
 * walking the schema tree. Every optional key is included, every array
 * is populated with one example item, enums resolve to their first
 * value, and `.default()` values are honored.
 *
 * Pure function -- no side effects, no schema mutation.
 */
export function zodToExample(schema: ZodTypeAny): unknown {
  const stack = new WeakSet<ZodTypeAny>();

  const resolve = (s: ZodTypeAny): unknown => {
    if (stack.has(s)) return null;
    stack.add(s);

    const resolver = RESOLVERS[s._def.typeName as ZodFirstPartyTypeKind];
    const result = resolver ? resolver(s._def as Record<string, unknown>, resolve) : null;

    stack.delete(s);
    return result;
  };

  return resolve(schema);
}
